import React, { useRef, useImperativeHandle } from "react";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import moment from "moment";

const getPrintTime = () => {
    const now = new Date();
    const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    };
    return now.toLocaleString("en-US", options);
};

const CancelKOTTemplate = React.forwardRef(({ order, items }, ref) => {
    const { CompanyName } = useSelector((state) => state.authReducer);

    // Organize items into parent-child structure
    const organizeOrderItems = (items) => {
        const parentItems = items.filter((item) => item.DealItemId === null);
        const result = [];

        for (const parent of parentItems) {
            const children = items.filter(
                (item) => item.RandomId === parent.RandomId && item.DealItemId !== null
            );
            result.push({
                parent,
                children,
            });
        }
        return result;
    };

    const organizedItems = organizeOrderItems(items || []);

    // Generate item rows with parent-child structure
    const itemBody = organizedItems?.map((group, groupIdx) => {
        const rows = [];

        // Parent item row
        rows.push(
            <div
                key={`parent-${groupIdx}`}
                className="flex justify-between text-sm text-black px-1 py-0.5"
            >
                <span className="w-[20%] font-semibold">{group.parent.Quantity || group.parent.quantity}</span>
                <span className="w-[60%] font-medium text-left">
                    {group.parent.ProductName || group.parent.name}
                    {group.parent.SizeName ? ` (${group.parent.SizeName})` : ""}
                    {group.parent.FlavourName ? ` ${group.parent.FlavourName}` : ""}
                </span>
            </div>
        );

        // Child item rows
        if (group.children?.length > 0) {
            group.children.forEach((child, childIdx) => {
                rows.push(
                    <div
                        key={`child-${groupIdx}-${childIdx}`}
                        className="flex justify-between text-sm text-black px-1 py-0.5"
                    >
                        <span className="w-[20%]"></span>
                        <span className="w-[60%] text-left pl-4 font-normal">
                            + {child.Quantity || child.quantity || 1} {child.ProductName || child.name}
                            {child.SizeName ? ` (${child.SizeName})` : ""}
                            {child.FlavourName ? ` ${child.FlavourName}` : ""}
                        </span>
                    </div>
                );
            });
        }

        return rows;
    }).flat();

    return (
        <div
            ref={ref}
            style={{
                fontFamily: "Calibri",
                fontSize: "12px",
                margin: "30px",
                display: "none"
            }}
            className="orderCancelKOT print-only"
        >
            <style>
                {`
          @media print {
            .print-only {
              display: block !important;
            }
            .no-print {
              display: none !important;
            }
          }
          @media screen {
            .print-only {
              display: none !important;
            }
          }
        `}
            </style>

            <div className="my-1 border-t-2 border-b-2 border-black text-center w-full">
                {/* Heading */}
                <div className="w-full text-center font-semibold text-black text-sm border-b-2 border-black bg-red-50">
                    KOT - CANCEL
                </div>

                {/* Order Mode Row */}
                <div className="flex justify-between text-sm text-black font-medium px-1">
                    <strong>Order Mode</strong>
                    <span>{order?.OrderMode || order?.orderModeName || "N/A"}</span>
                </div>

                {/* Customer Details */}
                <div className="w-full flex flex-col mt-1 text-left text-sm text-black px-1 ">
                    <div>
                        <strong>Customer:</strong> &nbsp; {order?.CustomerName || order?.customerName || "N/A"}
                    </div>
                    <div>
                        <strong>Address:</strong>&nbsp;  {order?.CompleteAddress || order?.address || "N/A"}
                    </div>
                    <div>
                        <strong>Phone:</strong> &nbsp; {order?.PhoneNumber || order?.phoneNumber || "N/A"}
                    </div>
                    {order?.AlternateNumber && (
                        <div>
                            <strong>Alt:</strong>&nbsp;  {order?.AlternateNumber}
                        </div>
                    )}
                    {order?.LandMark && (
                        <div>
                            <strong>LandMark:</strong>&nbsp;  {order?.LandMark}
                        </div>
                    )}
                </div>
            </div>

            <div className="w-full text-sm text-black px-1 ">
                <div className="flex justify-between font-medium">
                    <span>Order #</span>
                    <span>{order?.OrderNumber || order?.orderNumber || "N/A"}</span>
                </div>
                <div className="flex justify-between font-medium">
                    <span>Date/Time:</span>
                    <span>{order?.OrderDate ? moment(order.OrderDate).format("YYYY-MM-DD HH:mm:ss") : (order?.orderDate || "N/A")}</span>
                </div>
                <div className="flex justify-between font-medium text-red-600">
                    <span>Status:</span>
                    <span>CANCELLED</span>
                </div>
                {order?.CancelDate && (
                    <div className="flex justify-between font-medium text-red-600">
                        <span>Cancelled Date/Time:</span>
                        <span>{moment(order.CancelDate).format("YYYY-MM-DD HH:mm:ss")}</span>
                    </div>
                )}
            </div>

            <div className="w-full border-y-2 border-black my-1 px-1 py-1">
                <div className="flex justify-between font-medium text-black text-sm">
                    <span className="w-[20%]">Qty</span>
                    <span className="w-[60%] text-left">Item</span>
                    <span className="w-[20%] text-right"></span>
                </div>

                <div>{itemBody}</div>
            </div>

            <br />

            <div className="flex flex-col w-full items-center text-black">
                <span className="italic text-red-600 font-semibold">ORDER CANCELLED</span>
                <span className="italic">Powered by EatX</span>
                <span> Printed on: {getPrintTime()}</span>
            </div>
        </div>
    );
});

const OrderCancelKOT = React.forwardRef(({ orderDetails, orderItems, onAfterPrint }, ref) => {
    const componentRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `CancelKOT_${orderDetails?.OrderNumber || orderDetails?.orderNumber || "unknown"}`,
        pageStyle: "@page { size: auto; margin: 0mm; }",
        onAfterPrint: onAfterPrint || (() => { })
    });

    // Expose print function via ref
    useImperativeHandle(ref, () => ({
        print: () => {
            if (orderDetails && orderItems && orderItems.length > 0) {
                // Use requestAnimationFrame to maintain user action context
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        try {
                            handlePrint();
                        } catch (error) {
                            console.error("Error in OrderCancelKOT print:", error);
                        }
                    }, 100);
                });
            }
        }
    }));

    // Don't auto-print - only print via ref to control sequence

    return (
        <div>
            {/* Hidden cancel KOT template - only visible when printing */}
            <CancelKOTTemplate ref={componentRef} order={orderDetails} items={orderItems} />
        </div>
    );
});

export default OrderCancelKOT;

